import { MutableRefObject, RefObject, useEffect, useRef } from 'react'

import { useLastCallback } from '$hooks/use-last-callback'
import useBackgroundMode, {
  isBackgroundModeActive,
} from '$hooks/window/use-background-mode'

export default function useVideoAutoPause(
  playerRef: RefObject<HTMLVideoElement>,
  canPlay: boolean,
) {
  const canPlayRef = useRef<boolean>()
  canPlayRef.current = canPlay

  const { play, pause } = usePlayPause(playerRef)

  const unfreezePlaying = useLastCallback(() => {
    if (canPlayRef.current && !isFrozen()) {
      play()
    }
  })

  const unfreezePlayingOnRaf = useLastCallback(() => {
    unfreezePlaying()
  })
  useBackgroundMode(pause, unfreezePlayingOnRaf, !canPlay)

  const handlePlaying = useLastCallback(() => {
    if (!canPlayRef.current) {
      pause()
    }
  })

  useEffect(() => {
    if (canPlay) {
      if (!isFrozen()) {
        play()
      }
    } else {
      pause()
    }
  }, [canPlay, play, pause])

  return { handlePlaying }
}

function usePlayPause(mediaRef: React.RefObject<HTMLMediaElement>) {
  const shouldPauseRef = useRef(false)
  const isLoadingPlayRef = useRef(false)

  const play = useLastCallback(() => {
    shouldPauseRef.current = false
    if (
      mediaRef.current &&
      !isLoadingPlayRef.current &&
      document.body.contains(mediaRef.current)
    ) {
      isLoadingPlayRef.current = true
      mediaRef.current
        .play()
        .then(() => {
          isLoadingPlayRef.current = false
          if (shouldPauseRef.current) {
            mediaRef.current?.pause()
            shouldPauseRef.current = false
          }
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.warn(e)
        })
    }
  })

  const pause = useLastCallback(() => {
    if (isLoadingPlayRef.current) {
      shouldPauseRef.current = true
    } else {
      mediaRef.current?.pause()
    }
  })

  return { play, pause }
}

function isFrozen() {
  return isBackgroundModeActive()
}
